import { ChangeDetectionStrategy, Component, Input, type OnDestroy, type OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { type Observable, Subscription, filter } from 'rxjs';

import { type AppState, isNotBlank, unsubscribe } from '@fcom/core';
import { CmsCollection, CmsContentType } from '@fcom/core-api';
import { LoginStatus } from '@fcom/core-api/login/interfaces/login.interface';
import { loginStatus } from '@fcom/core/selectors/login.selector';
import { finShare, safeMap } from '@fcom/rx';
import { ButtonMode, ButtonTheme, ButtonType } from '@fcom/ui-components/components/buttons/button/enums';
import { toKebabCase } from '@fcom/ui-components/utils/string.utils';

export enum RadioLevels {
  LANDER_FORM = 'landerForm',
  TOP_LEVEL = 'topLevel',
  SUB_LEVEL = 'subLevel',
}

const validateLanderForm = (): ValidatorFn => {
  return (form: UntypedFormGroup): Record<string, boolean> | null => {
    if (
      form.get(RadioLevels.TOP_LEVEL)?.value === '' ||
      (form.get(RadioLevels.TOP_LEVEL)?.value?.contentType === CmsContentType.CMCollection &&
        form.get(RadioLevels.SUB_LEVEL)?.value === null)
    ) {
      return { mismatch: true };
    } else {
      return null;
    }
  };
};

/**
 * @deprecated Use the new `RadioItemGroupComponent` for simple radio lists.
 * For nested elements, use this one.
 */
@Component({
  selector: 'fin-radio-list',
  templateUrl: './radio-list.component.html',
  styleUrls: ['./radio-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RadioListComponent implements OnInit, OnDestroy {
  private store$ = inject<Store<AppState>>(Store);
  private router = inject(Router);
  private fb = inject(UntypedFormBuilder);

  readonly LoginStatus = LoginStatus;
  readonly ButtonMode = ButtonMode;
  readonly ButtonType = ButtonType;
  readonly ButtonTheme = ButtonTheme;
  readonly RadioLevels = RadioLevels;

  loginStatus$: Observable<LoginStatus>;
  form: UntypedFormGroup = new UntypedFormGroup({});
  group: UntypedFormGroup;

  private subscriptions: Subscription = new Subscription();

  @Input()
  content$: Observable<CmsCollection>;
  formTestId$: Observable<string | null>;

  ngOnInit(): void {
    this.formTestId$ = this.content$.pipe(
      safeMap((content: CmsCollection): string | null => {
        const formId = toKebabCase(content.teaserTitle);
        return formId ? `${formId}-form` : null;
      }),
      filter(isNotBlank)
    );
    this.loginStatus$ = this.store$.pipe(loginStatus(), finShare());
    const oldGroup = this.form;
    this.group = this.fb.group(
      {
        [RadioLevels.TOP_LEVEL]: [oldGroup?.get(RadioLevels.TOP_LEVEL)?.value || ''],
        [RadioLevels.SUB_LEVEL]: [oldGroup?.get(RadioLevels.SUB_LEVEL)?.value || ''],
      },

      { validators: [validateLanderForm()] }
    );

    this.form.setControl(RadioLevels.LANDER_FORM, this.group);

    this.subscriptions.add(this.group.get(RadioLevels.TOP_LEVEL).valueChanges.subscribe(() => this.topLevelChanged()));
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  private topLevelChanged = (): void => {
    this.group.get(RadioLevels.SUB_LEVEL).reset();
  };

  navigateBasedOnTheChoice = (): void => {
    const finalChoice =
      this.form.value.landerForm.topLevel.contentType === CmsContentType.CMCollection
        ? this.form.value.landerForm.subLevel
        : this.form.value.landerForm.topLevel;

    const finalUrl = finalChoice.localSettings.formType
      ? `${finalChoice.target.url}?formType=${finalChoice.localSettings.formType}`
      : finalChoice.target.url;

    this.router.navigateByUrl(finalUrl);
  };
}
